import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { DrawerBody, OverlayDrawer } from '@fluentui/react-components';
import { Dismiss20Regular } from '@fluentui/react-icons';
import { PostRaidItem } from '../../redux/raidSlice';
import { PostActionLog } from '../../redux/actionLogSlice';
import styles from '../TaskManagement/TaskFormSidebar.module.css';

/**
 * Lightweight create drawer for RAID or Action Log from Ask Lens answers.
 * kind: 'raid' | 'action_log'
 */
const AskAiQuickCreateSidebar = ({
    show,
    setShow,
    kind = 'raid',
    defaultText = '',
}) => {
    const dispatch = useDispatch();
    const raidLoading = useSelector(state => state?.raid?.buttonLoading);
    const actionLoading = useSelector(state => state?.actionLog?.buttonLoading);
    const loading = kind === 'raid' ? raidLoading : actionLoading;

    const [text, setText] = useState('');

    const title = kind === 'raid' ? 'Create RAID item' : 'Create Action Log entry';
    const placeholder =
        kind === 'raid'
            ? 'Describe the risk, issue, assumption, or dependency…'
            : 'Describe the action or decision to log…';
    const submitLabel = kind === 'raid' ? 'Create RAID' : 'Create Action Log';

    useEffect(() => {
        if (!show) return;
        const next =
            typeof defaultText === 'function' ? defaultText() : defaultText || '';
        setText(String(next || '').trim());
    }, [show, defaultText]);

    const handleClose = () => {
        setShow?.(false);
        setText('');
    };

    const handleSubmit = async () => {
        const value = text.trim();
        if (!value) {
            toast.error(kind === 'raid' ? 'Enter a RAID item.' : 'Enter an action.');
            return;
        }
        try {
            if (kind === 'raid') {
                await dispatch(PostRaidItem({ raid_item: value })).unwrap();
                toast.success('RAID item created.');
            } else {
                await dispatch(PostActionLog({ action_text: value })).unwrap();
                toast.success('Action Log entry created.');
            }
            handleClose();
        } catch (e) {
            const msg = e?.detail || e?.error || e?.message || 'Create failed';
            toast.error(typeof msg === 'string' ? msg : 'Create failed');
        }
    };

    return (
        <OverlayDrawer
            open={Boolean(show)}
            position="end"
            size="medium"
            onOpenChange={(_, data) => {
                if (!data.open) handleClose();
            }}>
            <DrawerBody>
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <h2 className={styles.sidebarTitle} style={{ marginBottom: 0 }}>
                        {title}
                    </h2>
                    <button
                        type="button"
                        className="btn btn-link p-0"
                        aria-label="Close"
                        onClick={handleClose}>
                        <Dismiss20Regular />
                    </button>
                </div>
                <label className="form-label" htmlFor="ask-ai-quick-create-text">
                    {kind === 'raid' ? 'RAID item' : 'Action'}
                </label>
                <textarea
                    id="ask-ai-quick-create-text"
                    className="form-control"
                    rows={10}
                    value={text}
                    onChange={e => setText(e.target.value)}
                    placeholder={placeholder}
                />
                <div className="d-flex justify-content-end gap-2 mt-3">
                    <button type="button" className="btn btn-light" onClick={handleClose}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        disabled={Boolean(loading)}
                        onClick={() => void handleSubmit()}>
                        {loading ? 'Saving…' : submitLabel}
                    </button>
                </div>
            </DrawerBody>
        </OverlayDrawer>
    );
};

export default AskAiQuickCreateSidebar;
